import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, addDays, subDays } from 'date-fns';

const { FiActivity, FiPackage, FiTrendingUp, FiLayers, FiImage } = FiIcons;

const InventoryHistory = ({ orders, varieties }) => {
  const historyData = orders.flatMap(order => {
    return order.items.map(item => {
      const variety = varieties.find(v => v.id === item.varietyId);
      const total = item.quantity + (item.bufferQuantity || 0);

      const base = new Date(order.baseDate);
      let deliveryDate;

      if (order.isSudden) {
        deliveryDate = base;
      } else if (order.dateType === 'delivery') {
        deliveryDate = base;
      } else {
        const totalCycle = (variety?.growingDays || 0) + (variety?.darkTreatmentDays || 0);
        deliveryDate = addDays(base, totalCycle);
      }

      return {
        customer: order.customer,
        variety: variety?.name || 'Unknown',
        varietyImage: variety?.image,
        orderQty: item.quantity,
        bufferQty: item.bufferQuantity || 0,
        totalQty: total,
        deliveryDate: deliveryDate,
        needPress: variety?.needPress || false,
        isSudden: order.isSudden
      };
    });
  }).sort((a, b) => b.deliveryDate - a.deliveryDate);

  const totalOrdered = historyData.filter(d => !d.isSudden).reduce((acc, curr) => acc + curr.orderQty, 0);
  const totalBuffered = historyData.reduce((acc, curr) => acc + curr.bufferQty, 0);
  const totalSuddenConsumed = historyData.filter(d => d.isSudden).reduce((acc, curr) => acc + curr.orderQty, 0);

  const currentAvailableBuffer = totalBuffered - totalSuddenConsumed;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8">
      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">Inventory History</h1>
        <p className="text-gray-500 mt-2 font-light">Historical audit of order volumes and reserve stock</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-[#16a34a]">
              <SafeIcon icon={FiPackage} />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Produced</span>
          </div>
          <p className="text-4xl font-light text-[#1e3a8a]">{totalOrdered + totalBuffered}</p>
          <p className="text-xs text-gray-400 mt-1">Total units grown</p>
        </div>
        <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center space-x-4 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentAvailableBuffer < 0 ? 'bg-red-50 text-red-500' : 'bg-orange-50 text-orange-500'}`}>
              <SafeIcon icon={FiLayers} />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Current Buffer</span>
          </div>
          <p className={`text-4xl font-light ${currentAvailableBuffer < 0 ? 'text-red-500' : 'text-[#1e3a8a]'}`}>{currentAvailableBuffer}</p>
          <p className="text-xs text-gray-400 mt-1">Remaining safety stock</p>
        </div>
        <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-[#1e3a8a]">
              <SafeIcon icon={FiTrendingUp} />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sudden Fulfillment</span>
          </div>
          <p className="text-4xl font-light text-[#1e3a8a]">
            {totalSuddenConsumed}
          </p>
          <p className="text-xs text-gray-400 mt-1">Units fulfilled from stock</p>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/30">
          <h3 className="text-xl font-light text-[#1e3a8a]">Production Log</h3>
          <div className="flex items-center space-x-2 text-xs font-bold text-gray-400">
            <SafeIcon icon={FiActivity} />
            <span>Sorted by Latest Delivery</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Delivery Date</th>
                <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Customer</th>
                <th className="px-8 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Variety</th>
                <th className="px-8 py-5 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest">Order</th>
                <th className="px-8 py-5 text-center text-[10px] font-bold text-orange-400 uppercase tracking-widest">Buffer</th>
                <th className="px-8 py-5 text-center text-[10px] font-bold text-[#16a34a] uppercase tracking-widest">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {historyData.map((row, idx) => (
                <tr key={idx} className={`hover:bg-gray-50/50 transition-colors ${row.isSudden ? 'bg-orange-50/10' : ''}`}>
                  <td className="px-8 py-6 text-sm font-medium text-gray-900">
                    {format(row.deliveryDate, 'MMM dd, yyyy')}
                  </td>
                  <td className="px-8 py-6 text-sm font-light text-gray-600">
                    {row.customer}
                  </td>
                  <td className="px-8 py-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {row.varietyImage ? (
                          <img src={row.varietyImage} alt={row.variety} className="w-full h-full object-cover" />
                        ) : (
                          <SafeIcon icon={FiImage} className="text-gray-300 text-xs" />
                        )}
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-gray-900">{row.variety}</span>
                        {row.needPress && (
                          <span className="text-[8px] font-bold text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded-full uppercase mt-0.5">Pressed</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6 text-center text-sm font-medium text-gray-700">
                    {row.orderQty}
                  </td>
                  <td className="px-8 py-6 text-center text-sm font-medium text-orange-500">
                    {row.bufferQty}
                  </td>
                  <td className="px-8 py-6 text-center">
                    {row.isSudden ? (
                      <span className="inline-block bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-bold text-[10px] uppercase tracking-wide">
                        Stock Deducted
                      </span>
                    ) : (
                      <span className="inline-block bg-green-50 text-[#16a34a] px-3 py-1 rounded-full font-bold text-[10px] uppercase tracking-wide">
                        Produced
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InventoryHistory;